/**
 * Metric Thresholds Configuration
 * Centralized configuration for dashboard metric warning and critical states
 * 
 * Adjust these values based on business requirements
 */

export type MetricVariant = 'default' | 'primary' | 'success' | 'warning' | 'error';

export interface ThresholdConfig {
  warning?: number;
  critical?: number;
}

/**
 * Metric thresholds
 * Values represent the point at which a metric becomes concerning
 */
export const METRIC_THRESHOLDS = {
  // Financial Metrics
  unpaidRent: {
    warning: 10000,    // €10,000 - Attention needed
    critical: 20000,   // €20,000 - Urgent action required
  },
  
  opexPercentage: {
    warning: 15,       // 15% of collected rent - Watch closely
    critical: 20,      // 20% of collected rent - Too high
  },
  
  // Portfolio Metrics
  contractsCreatedYTD: {
    warning: 5,        // Fewer than 5 contracts - May need more activity
    critical: 2,       // Fewer than 2 contracts - Concerning
  },
  
  acquisitionsInPipeline: {
    warning: 3,        // Fewer than 3 deals - Pipeline is thin
    critical: 1,       // Only 1 or no deals - Need more prospects
  },
  
  // Task Metrics
  tasksCompletedByUser: {
    warning: 3,        // Fewer than 3 tasks this week - Low productivity
  },
} as const;

/**
 * Evaluate a metric value against thresholds
 * @param value - The current metric value
 * @param thresholds - The threshold configuration
 * @param inverse - If true, lower values trigger warnings (default: higher values trigger warnings)
 * @returns The variant to apply to the metric card
 */
export function evaluateMetric(
  value: number,
  thresholds: ThresholdConfig,
  inverse: boolean = false
): MetricVariant {
  if (inverse) {
    // For metrics where LOW values are bad (e.g., contracts created, tasks completed)
    if (thresholds.critical !== undefined && value <= thresholds.critical) {
      return 'error';
    }
    if (thresholds.warning !== undefined && value <= thresholds.warning) {
      return 'warning';
    }
  } else {
    // For metrics where HIGH values are bad (e.g., unpaid rent, OPEX)
    if (thresholds.critical !== undefined && value >= thresholds.critical) {
      return 'error';
    }
    if (thresholds.warning !== undefined && value >= thresholds.warning) {
      return 'warning';
    }
  }
  
  return 'default';
}

/**
 * Get variant for specific metrics
 * These functions encapsulate the business logic for each metric
 */

export function getUnpaidRentVariant(unpaidRent: number): MetricVariant {
  return evaluateMetric(unpaidRent, METRIC_THRESHOLDS.unpaidRent);
}

export function getOpexVariant(opexPercentage: number): MetricVariant {
  return evaluateMetric(opexPercentage, METRIC_THRESHOLDS.opexPercentage);
}

export function getContractsCreatedVariant(contractCount: number): MetricVariant {
  return evaluateMetric(contractCount, METRIC_THRESHOLDS.contractsCreatedYTD, true);
}

export function getAcquisitionsVariant(acquisitionCount: number): MetricVariant {
  return evaluateMetric(acquisitionCount, METRIC_THRESHOLDS.acquisitionsInPipeline, true);
}

export function getTasksCompletedVariant(taskCount: number): MetricVariant {
  // Tasks are informational - show success if completed, no warning
  return taskCount > 0 ? 'success' : 'default';
}

/**
 * Get user-friendly explanation for why a metric is in warning/error state
 */
export function getMetricExplanation(
  metricType: keyof typeof METRIC_THRESHOLDS,
  value: number,
  variant: MetricVariant
): string | null {
  if (variant === 'default' || variant === 'success' || variant === 'primary') {
    return null;
  }

  const thresholds = METRIC_THRESHOLDS[metricType];
  
  switch (metricType) {
    case 'unpaidRent':
      if (variant === 'error' && 'critical' in thresholds && thresholds.critical) {
        return `Kritisch: Meer dan €${thresholds.critical.toLocaleString()} onbetaald`;
      }
      return `Let op: Meer dan €${thresholds.warning?.toLocaleString()} onbetaald`;
    
    case 'opexPercentage':
      if (variant === 'error' && 'critical' in thresholds && thresholds.critical) {
        return `Kritisch: OPEX is meer dan ${thresholds.critical}% van huur`;
      }
      return `Let op: OPEX nadert ${thresholds.warning}% van huur`;
    
    case 'contractsCreatedYTD':
      if (variant === 'error' && 'critical' in thresholds && thresholds.critical) {
        return `Kritisch: Minder dan ${thresholds.critical} contracten dit jaar`;
      }
      return `Let op: Weinig contracten dit jaar (< ${thresholds.warning})`;
    
    case 'acquisitionsInPipeline':
      if (variant === 'error') {
        return `Kritisch: Slechts ${value} acquisitie(s) in pipeline`;
      }
      return `Let op: Weinig acquisities in pipeline (< ${thresholds.warning})`;
    
    default:
      return null;
  }
}

